(function () {
  const SELECTORS = {
    modal: 'free-gift-modal',
    close: '[data-free-gift-close]',
    item: '[data-free-gift-item]',
    variant: '[data-free-gift-variant]',
    pick: '[data-free-gift-pick]',
    claim: '[data-free-gift-claim]',
    progress: '[data-free-gift-progress]',
    progressMessage: '[data-free-gift-progress-message]',
    progressMobileMessage: '[data-free-gift-progress-mobile-message]',
    progressToggle: '[data-free-gift-progress-toggle]',
    progressToggleIcon: '[data-free-gift-progress-toggle-icon]',
    progressClaim: '[data-free-gift-progress-claim]',
    progressClaimMobile: '[data-free-gift-progress-claim-mobile]',
    progressCheck: '[data-free-gift-progress-check]',
    progressTrack: '[data-free-gift-progress-track]',
    progressFill: '[data-free-gift-progress-fill]',
    progressGoal: '[data-free-gift-progress-goal]',
    productProgress: '[data-free-gift-progress][data-placement="product"]',
    cartForm: 'form[action*="/cart/add"]',
    shtCartDrawer: 'sht-cart-drwr',
    shtCartDrawerForm: 'sht-cart-drwr-frm',
    shtCartNotification: 'sht-cart-noti'
  };

  const CART_ENDPOINTS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];
  const CHECK_DELAY = 350;
  const PROPERTY_NAME = '_free_gift';
  const PROPERTY_VALUE = 'true';
  // Which surface granted a complimentary line. This modal is no longer the
  // only one: the Fitting Room configurator grants its own engraved nametag
  // when a set clears the same threshold, and stamps the same _free_gift
  // property. _free_gift therefore answers "is this a gift?" but NOT "is
  // this mine to mutate?" — and those are different questions, because
  // removeAllGifts()/enforceSingleGift() delete cart lines.
  const SOURCE_PROPERTY = '_free_gift_source';
  const SOURCE_SELF = 'free-gift-modal';
  // Written by whichever surface granted the line, naming the PAID variant
  // that same item should fall back to if the cart later stops qualifying.
  // The Fitting Room's complimentary nametag is a separate hidden $0
  // PRODUCT, not a discounted variant of the paid one — so a gift line can
  // only be "re-priced" by swapping it for that paid variant. Stripping
  // _free_gift alone would leave a permanently free line that no cleanup
  // path can even identify. The line carries its own upgrade path because
  // the gift->paid mapping lives in the configurator's section settings,
  // which this store-wide script cannot see.
  const UPGRADE_PROPERTY = '_free_gift_upgrade_variant';

  const modal = document.querySelector(SELECTORS.modal);
  if (!modal) return;

  let isChecking = false;
  let isMutatingGift = false;
  let selectedVariantId = null;
  let checkTimer = null;
  let lastProgressPercent = null;
  let lastKnownCart = null;

  const threshold = Number(modal.dataset.threshold || 0);
  const pickLabel = modal.dataset.pickLabel || 'Pick';
  const pickedLabel = modal.dataset.pickedLabel || 'Picked';
  const giftProducts = parseJsonAttribute(modal.dataset.giftProducts, []);
  const giftVariantIds = giftProducts.flatMap((product) => {
    return (product.variants || []).map((variant) => Number(variant.id));
  });

  if (!threshold || !giftVariantIds.length) return;

  // Single source of truth for the threshold, in the shop's BASE currency
  // cents. The Fitting Room prefers this over its own section setting, so
  // the two surfaces cannot drift apart when one is edited in the admin.
  window.LB_FREE_GIFT_THRESHOLD_BASE_CENTS = threshold;

  // Read LIVE on every call, never snapshotted. Shopify populates
  // window.Shopify.currency from an inline script, and this file is
  // deferred — a value cached at load time can be the pre-hydration
  // default. On a store presenting IDR (rate ~16000) a cached rate of 1
  // turns a Rp 3,200,000 threshold into Rp 200, i.e. every cart qualifies.
  // Number() also copes with the rate arriving as a string ("16000.0").
  function getCurrencyRate() {
    const rate = window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.rate;

    const parsedRate = Number(rate);
    return Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : 1;
  }

  // Threshold in the shopper's ACTIVE presentment currency, in sub-units —
  // the same units /cart.js reports final_line_price in, so the comparison
  // is apples to apples in every market. Zero-decimal currencies need no
  // special case: Shopify reports those in sub-units too.
  function getActiveThreshold() {
    return Math.round(threshold * getCurrencyRate());
  }

  function parseJsonAttribute(value, fallback) {
    if (!value) return fallback;

    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return JSON.parse(textarea.value);
    } catch (error) {
      console.warn('[free-gift] Could not parse gift products JSON.', error);
      return fallback;
    }
  }

  function getRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }

    return '/';
  }

  function buildCartUrl(path) {
    return getRootUrl() + path.replace(/^\//, '');
  }

  async function fetchJson(url, options) {
    const response = await fetch(url, options);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || response.statusText);
    }

    return response.json();
  }

  function getCart() {
    return fetchJson(buildCartUrl('cart.js'));
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD'
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function lineProperty(item, name) {
    return item && item.properties ? item.properties[name] : undefined;
  }

  // "Is this line a complimentary gift?" — whoever granted it.
  //
  // Matching on variant id alone meant a free nametag earned in the Fitting
  // Room was invisible here: hasGift() returned false, so checkCartState()
  // opened this modal and offered the shopper a SECOND free gift on top of
  // the one already in their bag, immediately after they added their set.
  function isGiftLine(item) {
    return giftVariantIds.includes(Number(item.variant_id)) ||
      String(lineProperty(item, PROPERTY_NAME)) === PROPERTY_VALUE;
  }

  // "Is this line MINE to change or delete?" — deliberately narrower.
  //
  // removeAllGifts() runs whenever the eligible subtotal drops below the
  // threshold and deletes every line it considers a gift. Widening
  // isGiftLine() without this second predicate would have swapped one bug
  // for a worse one: the modal would start deleting the Fitting Room's
  // complimentary tag out of the cart. A line is only ours when it carries
  // our own source stamp — or, for gift lines already sitting in shoppers'
  // carts from before this property existed, when it matches a configured
  // gift variant and names no other owner.
  function isModalManagedGift(item) {
    if (!isGiftLine(item)) return false;
    const source = lineProperty(item, SOURCE_PROPERTY);
    if (source) return String(source) === SOURCE_SELF;
    return giftVariantIds.includes(Number(item.variant_id));
  }

  function eligibleSubtotal(cart) {
    return cart.items
      .filter((item) => !isGiftLine(item))
      .reduce((total, item) => total + Number(item.final_line_price || 0), 0);
  }

  function giftLines(cart) {
    return cart.items.filter(isGiftLine);
  }

  // Only the subset this modal is allowed to mutate.
  function modalGiftLines(cart) {
    return cart.items.filter(isModalManagedGift);
  }

  function externalGiftLines(cart) {
    return cart.items.filter((item) => isGiftLine(item) && !isModalManagedGift(item));
  }

  function hasGift(cart) {
    return giftLines(cart).length > 0;
  }

  function interpolateMessage(template, values) {
    return Object.keys(values).reduce((message, key) => {
      return message.replace(new RegExp('{{\\s*' + key + '\\s*}}', 'g'), values[key]);
    }, template || '');
  }

  function updateText(element, text) {
    if (element && element.textContent !== text) {
      element.textContent = text;
    }
  }

  function captureCurrentProgressPercent() {
    const fill = document.querySelector(SELECTORS.progressFill);
    if (!fill) return;

    const track = fill.closest(SELECTORS.progressTrack);
    const width = parseFloat(fill.style.width || (track && track.getAttribute('aria-valuenow')));
    if (!Number.isNaN(width)) {
      lastProgressPercent = width;
    }
  }

  function applyAnimatedProgressWidth(fill, percent) {
    const targetPercent = Math.max(0, Math.min(percent, 100));

    if (
      lastProgressPercent !== null &&
      lastProgressPercent !== targetPercent &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      fill.style.transition = 'none';
      fill.style.width = lastProgressPercent + '%';
      fill.offsetWidth;

      window.requestAnimationFrame(() => {
        fill.style.transition = '';
        fill.style.width = targetPercent + '%';
      });
    } else {
      fill.style.width = targetPercent + '%';
    }
  }

  function animateRenderedProgressBars(root) {
    if (lastProgressPercent === null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const scope = root && root.querySelectorAll ? root : document;
    const fills = [];

    if (scope.matches && scope.matches(SELECTORS.progressFill)) {
      fills.push(scope);
    }

    fills.push(...scope.querySelectorAll(SELECTORS.progressFill));

    let animatedTargetPercent = null;

    fills.forEach((fill) => {
      const track = fill.closest(SELECTORS.progressTrack);
      const targetPercent = parseFloat(fill.style.width || (track && track.getAttribute('aria-valuenow')));

      if (Number.isNaN(targetPercent) || targetPercent === lastProgressPercent) return;

      applyAnimatedProgressWidth(fill, targetPercent);
      animatedTargetPercent = targetPercent;
    });

    if (animatedTargetPercent !== null) {
      lastProgressPercent = animatedTargetPercent;
    }
  }

  function updateProgressBars(cart) {
    const progressBars = document.querySelectorAll(SELECTORS.progress);
    if (!progressBars.length || !cart) return;

    lastKnownCart = cart;

    const activeThreshold = getActiveThreshold();
    const subtotal = eligibleSubtotal(cart);
    const remaining = Math.max(activeThreshold - subtotal, 0);
    const percent = activeThreshold > 0 ? Math.min(Math.round((subtotal / activeThreshold) * 100), 100) : 0;
    const unlocked = remaining === 0;
    const cartHasGift = hasGift(cart);

    progressBars.forEach((progress) => {
      const message = progress.querySelector(SELECTORS.progressMessage);
      const mobileMessage = progress.querySelector(SELECTORS.progressMobileMessage);
      const claim = progress.querySelector(SELECTORS.progressClaim);
      const claimMobile = progress.querySelector(SELECTORS.progressClaimMobile);
      const check = progress.querySelector(SELECTORS.progressCheck);
      const track = progress.querySelector(SELECTORS.progressTrack);
      const fill = progress.querySelector(SELECTORS.progressFill);
      const goal = progress.querySelector(SELECTORS.progressGoal);
      const remainingTemplate = progress.dataset.remainingMessage || '{{ amount }} left for the GIFT!';
      const remainingTemplateMobile = progress.dataset.remainingMessageMobile || 'Spend {{ amount }} and claim free name tag';
      const unlockedTemplate = progress.dataset.unlockedMessage || 'You unlocked a Free Nametag';
      const claimLabel = progress.dataset.claimLabel || 'CLAIM';

      progress.hidden = false;
      progress.classList.toggle('is-unlocked', unlocked);
      progress.classList.toggle('has-gift', cartHasGift);

      const progressText = unlocked
        ? interpolateMessage(unlockedTemplate, {
            amount: formatMoney(remaining),
            threshold: formatMoney(activeThreshold),
            total: formatMoney(subtotal)
          })
        : interpolateMessage(remainingTemplate, {
            amount: formatMoney(remaining),
            threshold: formatMoney(activeThreshold),
            total: formatMoney(subtotal)
      });

      const progressTextMobile = unlocked
        ? interpolateMessage(unlockedTemplate, {
            amount: formatMoney(remaining),
            threshold: formatMoney(activeThreshold),
            total: formatMoney(subtotal)
          })
        : interpolateMessage(remainingTemplateMobile, {
            amount: formatMoney(remaining),
            threshold: formatMoney(activeThreshold),
            total: formatMoney(subtotal)
      });

      if (message) {
        updateText(message, progressText);
      }

      if (mobileMessage) {
        updateText(mobileMessage, progressTextMobile);
      }

      if (claim) {
        updateText(claim, claimLabel);
        claim.hidden = !unlocked || cartHasGift;
      }

      if (claimMobile) {
        updateText(claimMobile, claimLabel);
        claimMobile.hidden = !unlocked || cartHasGift;
      }

      if (check) {
        check.hidden = !unlocked;
      }

      if (track) {
        track.setAttribute('aria-valuenow', String(percent));
      }

      if (fill) {
        applyAnimatedProgressWidth(fill, percent);
      }

      if (goal) {
        updateText(goal, formatMoney(activeThreshold));
      }

      progress.dataset.freeGiftNormalized = 'true';
    });

    lastProgressPercent = percent;
    updateProductProgressOffset();
  }

  function updateProductProgressOffset() {
    const productProgressBars = Array.from(document.querySelectorAll(SELECTORS.productProgress));
    if (!productProgressBars.length) return;

    const visibleHeight = productProgressBars.reduce((height, productProgress) => {
      const rect = productProgress.getBoundingClientRect();
      const styles = window.getComputedStyle(productProgress);
      const isVisible = styles.display !== 'none' && styles.visibility !== 'hidden' && rect.height > 0;

      return isVisible ? Math.max(height, Math.ceil(rect.height)) : height;
    }, 0);

    if (!visibleHeight) return;

    document.documentElement.style.setProperty('--free-gift-product-progress-offset', visibleHeight + 32 + 'px');
    document.body.classList.add('has-free-gift-product-progress');
  }

  function toggleProductProgress(progress) {
    if (!progress || progress.dataset.placement !== 'product') return;

    const isOpen = !progress.classList.contains('is-mobile-open');
    const toggle = progress.querySelector(SELECTORS.progressToggle);
    const icon = progress.querySelector(SELECTORS.progressToggleIcon);

    progress.classList.toggle('is-mobile-open', isOpen);

    if (toggle) {
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    if (icon) {
      icon.textContent = isOpen ? '-' : '+';
    }

    updateProductProgressOffset();
  }

  /* Is a guided configurator currently occupying the screen?
   *
   * The Lillebjörn Fitting Room is a multi-step set builder that dispatches
   * "cart:refresh" on add — which schedules a cart check here — and grants
   * its own complimentary nametag against this very threshold. Popping this
   * dialog over it interrupts a shopper mid-build to offer a reward they
   * have usually already earned.
   *
   * Three independent signals, any of which counts. The DOM check is last
   * and is the safety net: it holds even if the configurator's own script
   * failed to load, or an older build without the flags is deployed, so
   * suppression never depends on the two files shipping together.
   */
  function isConfiguratorActive() {
    if (window.LB_FR_ACTIVE) return true;
    if (document.body && document.body.classList.contains('lb-fr-open')) return true;
    return !!document.querySelector('lillebjorn-fitting-room');
  }

  // options.userInitiated — set only when the shopper explicitly asked for
  // the dialog (the progress bar's Claim button). Those are honoured
  // unconditionally: silently ignoring a control someone just pressed is a
  // worse failure than an ill-timed popup.
  function openModal(options) {
    const userInitiated = !!(options && options.userInitiated);

    if (!userInitiated && isConfiguratorActive()) {
      // Suppress silently — and make sure we are not already sitting open
      // on top of the configurator from a check that ran before it mounted.
      closeModal();
      return;
    }

    if (modal.hidden) {
      resetGiftSelection();
    }

    modal.hidden = false;
    document.documentElement.classList.add('free-gift-modal-open');

    const firstPickButton = modal.querySelector(SELECTORS.pick);
    if (firstPickButton) firstPickButton.focus({ preventScroll: true });
  }

  function closeModal() {
    modal.hidden = true;
    document.documentElement.classList.remove('free-gift-modal-open');
  }

  function getClaimButton() {
    return modal.querySelector(SELECTORS.claim);
  }

  function resetGiftSelection() {
    selectedVariantId = null;

    modal.querySelectorAll(SELECTORS.item).forEach((item) => {
      item.classList.remove('is-selected');
      item.removeAttribute('aria-selected');

      const pickButton = item.querySelector(SELECTORS.pick);
      if (pickButton) {
        pickButton.classList.remove('is-picked');
        pickButton.setAttribute('aria-pressed', 'false');
        pickButton.textContent = pickLabel;
      }
    });

    const claimButton = getClaimButton();
    if (claimButton) {
      claimButton.disabled = true;
    }
  }

  function selectGiftItem(item) {
    if (!item) return;

    const variantInput = item.querySelector(SELECTORS.variant);
    if (!variantInput || !variantInput.value) return;

    selectedVariantId = variantInput.value;

    modal.querySelectorAll(SELECTORS.item).forEach((giftItem) => {
      const isSelected = giftItem === item;
      giftItem.classList.toggle('is-selected', isSelected);
      giftItem.setAttribute('aria-selected', isSelected ? 'true' : 'false');

      const pickButton = giftItem.querySelector(SELECTORS.pick);
      if (pickButton) {
        pickButton.classList.toggle('is-picked', isSelected);
        pickButton.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        pickButton.textContent = isSelected ? pickedLabel : pickLabel;
      }
    });

    const claimButton = getClaimButton();
    if (claimButton) {
      claimButton.disabled = false;
    }
  }

  async function changeLine(key, quantity, includeSections) {
    const body = {
      id: key,
      quantity
    };
    const sections = includeSections ? getThemeSectionsToRender() : [];

    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    return fetchJson(buildCartUrl('cart/change.js'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });
  }

  // Removes only the gifts THIS modal granted. A complimentary line granted
  // by another surface (the Fitting Room's engraved nametag) is that
  // surface's to withdraw — deleting it here would silently strip a reward
  // the shopper watched themselves earn during configuration.
  async function removeAllGifts(cart) {
    const gifts = modalGiftLines(cart);
    // Returns a COUNT, not just a cart. checkCartState() used to announce
    // "free-gift:removed" + "cart:updated" on every below-threshold pass
    // whether or not anything changed — and cart:updated is one of its own
    // five triggers, so a sub-threshold cart (an EMPTY one included) drove
    // an endless ~350ms fetch loop for as long as the page was open.
    if (!gifts.length) return { cart, removed: 0 };

    let updatedCart = cart;
    let removed = 0;
    for (const item of gifts) {
      updatedCart = await changeLine(item.key, 0, true);
      removed += 1;
    }

    return { cart: updatedCart, removed };
  }

  // Properties to carry across when a gift line is re-priced: everything
  // the shopper authored (engraving name, phone, "None — blank tag") and
  // any private metadata, minus the three markers describing gift status.
  function carriedProperties(item) {
    const source = (item && item.properties) || {};
    const carried = {};
    Object.keys(source).forEach((key) => {
      if (key === PROPERTY_NAME || key === SOURCE_PROPERTY || key === UPGRADE_PROPERTY) return;
      carried[key] = source[key];
    });
    return carried;
  }

  function samePropertyBag(a, b) {
    const aKeys = Object.keys(a).sort();
    const bKeys = Object.keys(b).sort();
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every((key, i) => bKeys[i] === key && String(a[key]) === String(b[key]));
  }

  // Idempotency guard. If a previous pass added the paid twin but failed to
  // retire the gift line, adding again would leave the shopper with two
  // paid nametags. Recognising the twin lets the retry finish the job
  // instead of repeating the half of it that already succeeded.
  function findPaidTwin(cart, variantId, carried) {
    return cart.items.find((item) => {
      return Number(item.variant_id) === Number(variantId) &&
        !isGiftLine(item) &&
        samePropertyBag(carriedProperties(item), carried);
    }) || null;
  }

  /* Option B — retain, don't delete.
   *
   * A complimentary line granted by another surface is that surface's to
   * withdraw, and this modal deliberately never deletes one. That left a
   * revenue leak: earn the free nametag at $250, drop the cart to $150 at
   * /cart, and the tag stayed free with nothing on the page able to take
   * it back (the configurator is not mounted there).
   *
   * So it is re-priced instead of removed: the engraving the shopper typed
   * survives, they simply now pay for it. Order is add-then-remove so a
   * failure leaves them holding their tag rather than nothing.
   */
  async function upgradeExternalGifts(cart) {
    const candidates = externalGiftLines(cart).filter((item) => {
      return Number(lineProperty(item, UPGRADE_PROPERTY)) > 0;
    });

    if (!candidates.length) return { cart, upgraded: 0 };

    let updatedCart = cart;
    let upgraded = 0;

    // Blocks re-entrant checkCartState() runs for the duration of the swap,
    // so a cart event mid-flight cannot start a second upgrade of the same
    // line.
    isMutatingGift = true;
    try {
      for (const item of candidates) {
        const paidVariantId = Number(lineProperty(item, UPGRADE_PROPERTY));
        const carried = carriedProperties(item);

        if (!findPaidTwin(updatedCart, paidVariantId, carried)) {
          await fetchJson(buildCartUrl('cart/add.js'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: paidVariantId,
                quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
                properties: carried
              }]
            })
          });
        }

        updatedCart = await changeLine(item.key, 0, true);
        upgraded += 1;
      }
    } catch (error) {
      // Never fatal: the shopper keeps the tag they earned and the next
      // cart event retries. Silence here would be worse than a free tag.
      console.error('[free-gift] Could not re-price a complimentary line.', error);
    } finally {
      isMutatingGift = false;
    }

    return { cart: updatedCart, upgraded };
  }

  async function enforceSingleGift(cart) {
    const gifts = modalGiftLines(cart);
    if (!gifts.length) return cart;

    let updatedCart = cart;
    // An externally-granted gift already satisfies the offer, so every
    // modal-owned gift alongside it is a duplicate — stand down rather than
    // leaving the shopper with two free items for one threshold.
    let keptGift = externalGiftLines(cart).length > 0;

    for (const item of gifts) {
      if (!keptGift) {
        keptGift = true;

        if (item.quantity > 1) {
          updatedCart = await changeLine(item.key, 1);
        }
      } else {
        updatedCart = await changeLine(item.key, 0);
      }
    }

    return updatedCart;
  }

  function getThemeSectionsToRender() {
    const cartDrawer = document.querySelector(SELECTORS.shtCartDrawer);
    const cartNotification = document.querySelector(SELECTORS.shtCartNotification);
    const sections = [];

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      sections.push(...cartDrawer.getSectionsToRender());
    }

    if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
      sections.push(...cartNotification.getSectionsToRender());
    }

    const sectionIds = sections
      .map((section) => section && section.section)
      .filter(Boolean);

    return Array.from(new Set(sectionIds));
  }

  function openThemeCartDrawer() {
    const cartDrawer = document.querySelector(SELECTORS.shtCartDrawer);
    if (!cartDrawer) return;

    ['open', 'show', 'showDrawer', 'openDrawer'].some((methodName) => {
      if (typeof cartDrawer[methodName] === 'function') {
        cartDrawer[methodName]();
        return true;
      }

      return false;
    });
  }

  function renderThemeCart(sectionResponse, cart, options) {
    if (!sectionResponse || !sectionResponse.sections) return;

    const renderOptions = {
      openDrawer: true,
      ...(options || {})
    };
    const cartDrawerForm = document.querySelector(SELECTORS.shtCartDrawerForm);
    const cartDrawer = document.querySelector(SELECTORS.shtCartDrawer);
    const cartNotification = document.querySelector(SELECTORS.shtCartNotification);
    const renderedState = {
      ...cart,
      sections: sectionResponse.sections
    };

    if (cartDrawerForm && typeof cartDrawerForm.renderContents === 'function') {
      cartDrawerForm.renderContents(renderedState);
    } else if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(renderedState);
    }

    if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
      cartNotification.getSectionsToRender().forEach((section) => {
        const target = section.space_selector && document.querySelector(section.space_selector);

        if (
          section.id === 'header-cart-status' &&
          target &&
          typeof cartNotification.getSectionInnerHTML === 'function' &&
          sectionResponse.sections[section.id]
        ) {
          target.innerHTML = cartNotification.getSectionInnerHTML(
            sectionResponse.sections[section.id],
            section.selector
          );
        }
      });
    }

    if (renderOptions.openDrawer) {
      openThemeCartDrawer();
    }
  }

  async function addGift(variantId) {
    if (!variantId || isMutatingGift) return;

    isMutatingGift = true;
    modal.classList.add('is-loading');

    try {
      let cart = await getCart();
      cart = await enforceSingleGift(cart);

      if (eligibleSubtotal(cart) < getActiveThreshold() || hasGift(cart)) {
        closeModal();
        return;
      }

      const sections = getThemeSectionsToRender();
      const body = {
        items: [
          {
            id: Number(variantId),
            quantity: 1,
            properties: {
              [PROPERTY_NAME]: PROPERTY_VALUE,
              // Claim ownership explicitly so isModalManagedGift() can tell
              // our lines from another surface's.
              [SOURCE_PROPERTY]: SOURCE_SELF
            }
          }
        ]
      };

      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      const addResponse = await fetchJson(buildCartUrl('cart/add.js'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      });

      const updatedCart = await getCart();

      closeModal();
      renderThemeCart(addResponse, updatedCart);
      updateProgressBars(updatedCart);
      scheduleCartCheck();
      document.dispatchEvent(new CustomEvent('free-gift:added', {
        bubbles: true,
        detail: {
          cart: updatedCart,
          sections: addResponse.sections || {}
        }
      }));
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: updatedCart
      }));
    } catch (error) {
      console.error('[free-gift] Could not add gift.', error);
    } finally {
      modal.classList.remove('is-loading');
      isMutatingGift = false;
    }
  }

  async function checkCartState() {
    if (isChecking || isMutatingGift) return;

    isChecking = true;

    try {
      let cart = await getCart();
      const subtotal = eligibleSubtotal(cart);

      if (subtotal < getActiveThreshold()) {
        closeModal();

        // 1. Another surface's gift is re-priced, never deleted (Option B).
        const upgrade = await upgradeExternalGifts(cart);
        cart = upgrade.cart;

        // 2. Our own gifts are withdrawn outright.
        const removal = await removeAllGifts(cart);
        cart = removal.cart;

        const changed = upgrade.upgraded > 0 || removal.removed > 0;

        // Unconditional: pure local bookkeeping, dispatches nothing, and
        // gating it would desync the progress bar from the cart.
        updateProgressBars(cart);

        // THE LOOP GUARD. Everything below announces a cart mutation, and
        // "cart:updated" is one of this function's own triggers — so
        // announcing when nothing changed re-entered here every 350ms
        // forever on any sub-threshold cart, an empty one included. Now the
        // chain is only extended when the cart genuinely moved, which is
        // self-limiting: the follow-up pass finds nothing left to do and
        // stops.
        if (!changed) return;

        renderThemeCart(cart, cart, { openDrawer: false });
        document.dispatchEvent(new CustomEvent('free-gift:removed', {
          bubbles: true,
          detail: {
            cart,
            sections: cart.sections || {},
            upgraded: upgrade.upgraded,
            removed: removal.removed
          }
        }));
        document.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: cart
        }));
        return;
      }

      cart = await enforceSingleGift(cart);
      updateProgressBars(cart);

      if (!hasGift(cart)) {
        openModal();
      } else {
        closeModal();
      }
    } catch (error) {
      console.error('[free-gift] Could not check cart.', error);
    } finally {
      isChecking = false;
    }
  }

  function scheduleCartCheck(delay) {
    window.clearTimeout(checkTimer);
    checkTimer = window.setTimeout(checkCartState, typeof delay === 'number' ? delay : CHECK_DELAY);
  }

  function isCartObject(value) {
    return value && Array.isArray(value.items);
  }

  function normalizeProgressFromCart(cart) {
    if (!isCartObject(cart)) return;

    lastKnownCart = cart;
    window.requestAnimationFrame(() => {
      updateProgressBars(cart);
    });
  }

  function isCartMutationUrl(url) {
    if (!url) return false;

    const stringUrl = typeof url === 'string' ? url : url.url;
    if (!stringUrl) return false;

    return CART_ENDPOINTS.some((endpoint) => stringUrl.includes(endpoint));
  }

  function patchFetch() {
    if (!window.fetch || window.fetch.__freeGiftPatched) return;

    const originalFetch = window.fetch;

    window.fetch = function patchedFetch(input, init) {
      if (isCartMutationUrl(input)) {
        captureCurrentProgressPercent();
      }

      const promise = originalFetch.apply(this, arguments);

      if (isCartMutationUrl(input)) {
        promise
          .then((response) => {
            if (!response || typeof response.clone !== 'function') return;

            response.clone().json()
              .then(normalizeProgressFromCart)
              .catch(() => {});
          })
          .catch(() => {});

        promise.finally(scheduleCartCheck);
      }

      return promise;
    };

    window.fetch.__freeGiftPatched = true;
  }

  function patchXhr() {
    if (!window.XMLHttpRequest || window.XMLHttpRequest.__freeGiftPatched) return;

    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function patchedOpen(method, url) {
      this.__freeGiftUrl = url;
      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function patchedSend() {
      if (isCartMutationUrl(this.__freeGiftUrl)) {
        captureCurrentProgressPercent();
        this.addEventListener('loadend', () => {
          try {
            normalizeProgressFromCart(JSON.parse(this.responseText));
          } catch (error) {
            // The cart endpoint can return non-cart payloads, for example cart/add.
          }

          scheduleCartCheck();
        });
      }

      return originalSend.apply(this, arguments);
    };

    window.XMLHttpRequest.__freeGiftPatched = true;
  }

  function observeProgressRenders() {
    if (!window.MutationObserver || !document.body) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const hasProgress = (node.matches && node.matches(SELECTORS.progress)) ||
              (node.querySelector && node.querySelector(SELECTORS.progress));

            if (hasProgress) {
              if (lastKnownCart) {
                updateProgressBars(lastKnownCart);
              }
              scheduleCartCheck(0);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function bindEvents() {
    modal.addEventListener('change', (event) => {
      if (event.target.matches(SELECTORS.variant)) {
        const item = event.target.closest(SELECTORS.item);

        if (item && item.classList.contains('is-selected')) {
          selectedVariantId = event.target.value;
        }
      }
    });

    modal.addEventListener('click', (event) => {
      if (event.target.closest(SELECTORS.close)) {
        closeModal();
        return;
      }

      const pickButton = event.target.closest(SELECTORS.pick);
      if (pickButton) {
        const item = pickButton.closest(SELECTORS.item);
        selectGiftItem(item);
        return;
      }

      const claimButton = event.target.closest(SELECTORS.claim);
      if (claimButton && !claimButton.disabled) {
        addGift(selectedVariantId);
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target;

      if (form && form.matches && form.matches(SELECTORS.cartForm)) {
        scheduleCartCheck();
      }
    }, true);

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest(SELECTORS.progressToggle);
      if (toggle) {
        toggleProductProgress(toggle.closest(SELECTORS.productProgress));
        return;
      }

      if (event.target.closest(SELECTORS.progressClaim) || event.target.closest(SELECTORS.progressClaimMobile)) {
        // Explicit request — never suppressed.
        openModal({ userInitiated: true });
      }
    });

    // Normal behaviour resumes when the configurator leaves the page. On the
    // usual path this never fires — the Fitting Room navigates to /cart, and
    // the fresh page evaluates from scratch. It matters for the teardown
    // cases that do NOT reload: a theme-editor section swap, or any future
    // drawer-mounted configurator.
    //
    // Routed through the same scheduleCartCheck debounce as every other
    // trigger, so an exit that coincides with a cart event collapses into
    // ONE evaluation rather than two — no double-trigger, no flash.
    document.addEventListener('lillebjorn:fitting-room-closed', scheduleCartCheck);

    document.addEventListener('cart:updated', scheduleCartCheck);
    document.addEventListener('cart:change', scheduleCartCheck);
    document.addEventListener('cart:refresh', scheduleCartCheck);
    document.addEventListener('ajaxProduct:added', scheduleCartCheck);
    document.addEventListener('product:added', scheduleCartCheck);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        closeModal();
      }
    });

    window.addEventListener('resize', updateProductProgressOffset);
  }

  patchFetch();
  patchXhr();
  observeProgressRenders();
  bindEvents();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      updateProductProgressOffset();
      checkCartState();
    }, { once: true });
  } else {
    updateProductProgressOffset();
    checkCartState();
  }
})();
